import { useState, useEffect, useMemo, useRef } from "react";
import { X, Tag as TagIcon } from "lucide-react";
import {
  listTags,
  listOrganizationTags,
  addTagToOrganization,
  removeTagFromOrganization,
  createTag,
  type Tag as TagType,
  type OrganizationTag,
} from "../../api/tags";
import { IconButton } from "../primitives/IconButton";
import { cn } from "../utils/cn";

type TagsManagerProps = {
  organizationId: string;
  organizationType: "client" | "ambassador";
  onUpdate?: () => void;
};

/**
 * Étiquettes (tags) en UX moderne : clic dans la zone = mode ajout,
 * liste filtrée au fil de la saisie, clic ou Entrée pour ajouter,
 * Entrée sur un libellé absent = création en base puis ajout.
 */
export function TagsManager({ organizationId, organizationType, onUpdate }: TagsManagerProps) {
  const [orgTags, setOrgTags] = useState<OrganizationTag[]>([]);
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const assignedTagIds = useMemo(() => new Set(orgTags.map((ot) => ot.tag_id)), [orgTags]);
  const availableTags = useMemo(
    () => allTags.filter((tag) => !assignedTagIds.has(tag.id)),
    [allTags, assignedTagIds]
  );
  const filteredSuggestions = useMemo(() => {
    if (!inputValue.trim()) return availableTags;
    const q = inputValue.trim().toLowerCase();
    return availableTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(q) ||
        tag.description?.toLowerCase().includes(q)
    );
  }, [availableTags, inputValue]);

  const exactMatch = useMemo(
    () =>
      inputValue.trim() &&
      filteredSuggestions.some((t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()),
    [filteredSuggestions, inputValue]
  );
  const canCreateNew = inputValue.trim() && !filteredSuggestions.some((t) => t.name === inputValue.trim());

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    listOrganizationTags(organizationId)
      .then((tags) => {
        if (!mounted) return;
        setOrgTags(tags);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load organization tags:", err);
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [organizationId]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    listTags()
      .then((data) => {
        if (!mounted) return;
        setAllTags(data.items);
      })
      .catch((err) => console.error("Failed to load tags:", err));
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setHighlightIndex(0);
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setInputValue("");
        setError(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen]);

  const refreshOrgTags = async () => {
    const updated = await listOrganizationTags(organizationId);
    setOrgTags(updated);
    onUpdate?.();
  };

  const handleAddTag = async (tagId: string) => {
    setError(null);
    setSaving(true);
    try {
      await addTagToOrganization(organizationId, tagId);
      await refreshOrgTags();
      setInputValue("");
      if (filteredSuggestions.length <= 1 && !canCreateNew) setIsOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'ajout du tag");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndAdd = async () => {
    const name = inputValue.trim();
    if (!name) return;
    setError(null);
    setSaving(true);
    try {
      const newTag = await createTag({
        name,
        color: null,
        ambassador_org_ids: [],
      });
      await addTagToOrganization(organizationId, newTag.id);
      await refreshOrgTags();
      setAllTags((prev) => [...prev, newTag]);
      setInputValue("");
      setIsOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la création du tag");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    setError(null);
    try {
      await removeTagFromOrganization(organizationId, tagId);
      await refreshOrgTags();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la suppression du tag");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const selected = filteredSuggestions[highlightIndex];
      if (selected) {
        handleAddTag(selected.id);
        return;
      }
      if (canCreateNew) {
        handleCreateAndAdd();
      }
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setInputValue("");
      setError(null);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) =>
        Math.min(i + 1, canCreateNew ? filteredSuggestions.length : filteredSuggestions.length - 1)
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted">Chargement des étiquettes…</div>;
  }

  return (
    <div className="space-y-3" ref={containerRef}>
      <h4 className="text-sm font-semibold text-text flex items-center gap-2">
        <TagIcon size={14} strokeWidth={1.5} />
        Étiquettes
      </h4>

      <div
        className={cn(
          "min-h-9 rounded-md border border-border bg-surface px-2 py-1.5 flex flex-wrap items-center gap-1.5",
          isOpen && "ring-2 ring-primary/30 border-primary/50"
        )}
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {orgTags.map((orgTag) => (
          <span
            key={orgTag.id}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-muted px-2.5 py-0.5 text-xs text-text"
          >
            {orgTag.tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(orgTag.tag_id);
              }}
              className="rounded p-0.5 hover:bg-danger/10 text-muted hover:text-danger"
              aria-label={`Retirer ${orgTag.tag.name}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher ou créer une étiquette…"
            aria-label="Ajouter une étiquette"
            className="flex-1 min-w-[160px] bg-transparent border-none outline-none text-sm text-text placeholder:text-muted py-0.5"
          />
        ) : (
          <span className="text-sm text-muted cursor-text py-0.5">
            Cliquer pour ajouter une étiquette…
          </span>
        )}
      </div>

      {isOpen && (filteredSuggestions.length > 0 || canCreateNew) && (
        <div
          className="rounded-md border border-border bg-surface shadow-lg py-1 max-h-48 overflow-y-auto z-10"
          role="listbox"
        >
          {filteredSuggestions.map((tag, i) => (
            <button
              key={tag.id}
              type="button"
              role="option"
              aria-selected={i === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-text hover:bg-surface-muted",
                i === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(i)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddTag(tag.id);
              }}
            >
              <div className="font-medium">{tag.name}</div>
              {tag.description && (
                <div className="text-xs text-muted mt-0.5">{tag.description}</div>
              )}
            </button>
          ))}
          {canCreateNew && (
            <button
              type="button"
              role="option"
              aria-selected={filteredSuggestions.length === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-primary hover:bg-surface-muted",
                filteredSuggestions.length === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(filteredSuggestions.length)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCreateAndAdd();
              }}
            >
              + Créer « {inputValue.trim()} » et l&apos;ajouter
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-danger bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}
      {saving && (
        <div className="text-xs text-muted">Enregistrement…</div>
      )}
    </div>
  );
}
